import React, { useState, useEffect } from 'react';
import GnomeChat from '../gnomes/GnomeChat';
import { useGnomeContext } from '../../context/GnomeContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { FiChevronLeft, FiMenu } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const MobileChat = () => {
    const { gnomes, selectedGnome, openChat, closeChat } = useGnomeContext();
    const { activeWorkspace } = useWorkspace();
    const [view, setView] = useState('list'); // 'list' | 'chat'

    // If context opens a chat externally (e.g. from notification), switch to chat view
    useEffect(() => {
        if (selectedGnome) {
            setView('chat');
        }
    }, [selectedGnome]);

    const handleBack = () => {
        closeChat();
        setView('list');
    };

    if (view === 'chat' && selectedGnome) {
        return (
            <div className="flex flex-col h-full bg-charcoal-50 dark:bg-black relative transition-colors duration-200">
                {/* Mobile Chat Container - Full Screen with Overlay Header */}
                <div className="flex-1 overflow-hidden relative">
                    <GnomeChat
                        key={`${activeWorkspace?.id}-${selectedGnome?.id}`}
                        gnome={selectedGnome}
                        hideHeader={true}
                        onBack={handleBack}
                    />

                    {/* Floating Header Actions - Overlay Top Left */}
                    <div className="absolute top-[env(safe-area-inset-top)] left-4 z-20 flex items-center gap-2 mt-2">
                        <button
                            onClick={handleBack}
                            className="w-10 h-10 bg-white/90 dark:bg-charcoal-800/90 backdrop-blur-md border border-charcoal-200 dark:border-charcoal-700 rounded-full flex items-center justify-center text-charcoal-600 dark:text-charcoal-300 shadow-sm hover:scale-105 transition-transform"
                            aria-label="Exit Chat"
                        >
                            <FiChevronLeft size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // List View
    // Filter out "The Agency"/"The Team" (gnomeId 0) from the mobile chat list
    const visibleGnomes = gnomes.filter(g => Number(g.id) !== 0 && g.title !== 'The Agency' && g.title !== 'Team Chat');

    return (
        <div className="flex flex-col min-h-full bg-charcoal-50 dark:bg-black pt-[env(safe-area-inset-top)] transition-colors duration-200">
            <div className="px-6 py-6 pb-2">
                <h1 className="text-2xl font-bold text-charcoal-900 dark:text-white">Messages</h1>
                <p className="text-sm text-charcoal-500 dark:text-charcoal-400 mt-1">Chat with your AI team</p>
            </div>

            <div className="px-4 py-2 space-y-3">
                {visibleGnomes.map((gnome) => (
                    <button
                        key={gnome.id}
                        onClick={() => openChat(gnome)}
                        className="w-full bg-white dark:bg-charcoal-800 p-4 rounded-xl border border-charcoal-200 dark:border-charcoal-700 shadow-sm flex items-center gap-4 hover:bg-charcoal-50 dark:hover:bg-charcoal-700 transition-colors active:scale-[0.98]"
                    >
                        <div className={`w-12 h-12 rounded-full ${gnome.displayColor || 'bg-charcoal-500'} flex items-center justify-center text-white shadow-sm ring-2 ring-charcoal-100 dark:ring-[#1a1a1a] overflow-hidden`}>
                             {gnome.headshot ? (
                                <img src={gnome.headshot} alt={gnome.displayName} className="w-full h-full object-cover" />
                             ) : (
                                <SafeIcon icon={gnome.icon} size={20} />
                             )}
                        </div>
                        <div className="flex-1 text-left">
                            <h3 className="font-bold text-charcoal-900 dark:text-charcoal-200 text-base">{gnome.displayName}</h3>
                            <p className="text-xs text-charcoal-500 dark:text-charcoal-400 truncate">{gnome.focus}</p>
                        </div>
                        <div className="w-2 h-2 rounded-full bg-blue-500 opacity-0 group-hover:opacity-100"></div>
                        {/* Could add unread indicator here later */}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default MobileChat;
